import React, { useEffect, useState } from 'react';
import { api } from '../app/api';
import { logger } from '../lib/logger';
import { ArrowRight, BookOpen } from 'lucide-react';

/**
 * LatestJournalSection (Phase 27)
 *
 * Homepage promotion for The Apex Journal. Pulls the three most recent
 * published posts from /api/blog/posts and surfaces them between the
 * Testimonials and Final CTA sections.
 *
 * Why this exists:
 *   The blog was previously only linked from the footer, which suppressed
 *   readership and SEO juice. This block gives recent posts a real shot at
 *   being seen by every homepage visitor without crowding the navbar.
 *
 * Implementation notes:
 *   • Uses the existing public list endpoint — no new API surface.
 *   • Has explicit loading, empty, and error states so a broken fetch
 *     never produces a blank gap on the homepage.
 *   • If the API returns zero posts, the entire section short-circuits to
 *     `null` rather than rendering an awkward "no posts" placeholder on the
 *     marketing site.
 */
const SECTION_LIMIT = 3;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return ''; }
};

const Skeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-2xl border border-border/60 overflow-hidden bg-card">
        <div className="aspect-[16/10] bg-secondary/60 animate-pulse" />
        <div className="p-5 space-y-3">
          <div className="h-3 w-20 bg-secondary/60 rounded animate-pulse" />
          <div className="h-5 w-4/5 bg-secondary/60 rounded animate-pulse" />
          <div className="h-3 w-full bg-secondary/40 rounded animate-pulse" />
          <div className="h-3 w-3/4 bg-secondary/40 rounded animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

export const LatestJournalSection = () => {
  const [posts, setPosts] = useState(null); // null = loading, [] = none, [..] = ready
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`/api/blog/posts?limit=${SECTION_LIMIT}`);
        if (cancelled) return;
        setPosts(Array.isArray(data?.posts) ? data.posts : []);
      } catch (err) {
        logger.warn('LatestJournalSection fetch failed', err);
        if (!cancelled) { setError(true); setPosts([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Loading state: show skeletons so the homepage doesn't visibly jump.
  const isLoading = posts === null;

  // If the API explicitly returns zero posts (and we're not loading),
  // hide the section entirely — better than an empty rectangle.
  if (!isLoading && !error && Array.isArray(posts) && posts.length === 0) {
    return null;
  }
  // On hard error we also hide rather than render a broken module.
  if (error && (!posts || posts.length === 0)) return null;

  return (
    <section
      className="bg-background py-16 sm:py-20"
      aria-labelledby="latest-journal-heading"
      data-testid="latest-journal-section"
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-bold mb-2 inline-flex items-center gap-1.5">
              <BookOpen className="h-3 w-3" />
              The Apex Journal
            </div>
            <h2
              id="latest-journal-heading"
              className="font-heading text-2xl sm:text-[1.95rem] font-bold tracking-tight leading-[1.15]"
            >
              Mental performance, in essay form.
            </h2>
            <p className="text-[14px] sm:text-[14.5px] text-foreground/60 mt-2 max-w-[58ch] leading-relaxed">
              Short reads from Coach Lewis on the mindset, habits, and small protocols that change how athletes show up under pressure.
            </p>
          </div>
          <a
            href="/blog"
            className="self-start sm:self-auto inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.14em] text-[#004C54] hover:text-[#003a40] transition-colors"
            data-testid="latest-journal-view-all"
          >
            Explore all essays
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        {isLoading ? (
          <Skeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {posts.slice(0, SECTION_LIMIT).map((p) => {
              const href = `/blog/${p.slug}`;
              const dateLabel = formatDate(p.published_at || p.created_at);
              return (
                <a
                  key={p.id || p.slug}
                  href={href}
                  data-testid={`latest-journal-card-${p.slug}`}
                  className="group rounded-2xl border border-border/60 overflow-hidden bg-card hover:bg-card/80 transition-colors hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004C54] focus-visible:ring-offset-2"
                >
                  <div className="aspect-[16/10] bg-secondary/40 overflow-hidden">
                    {p.hero_image_url ? (
                      <img
                        src={p.hero_image_url}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-foreground/30">
                        <BookOpen className="h-7 w-7" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 text-[10.5px] font-mono-accent uppercase tracking-[0.18em] text-foreground/55 font-semibold">
                      {p.tags?.[0] && (
                        <span className="text-[#004C54]">#{p.tags[0]}</span>
                      )}
                      {p.tags?.[0] && dateLabel && <span aria-hidden="true">·</span>}
                      {dateLabel && <span>{dateLabel}</span>}
                    </div>
                    <h3 className="font-heading text-[17px] sm:text-[18px] font-bold tracking-tight leading-snug mb-2 line-clamp-2 group-hover:text-[#004C54] transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="text-[13.5px] text-foreground/65 leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                    )}
                    <div className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-bold uppercase tracking-[0.14em] text-[#004C54]">
                      Read
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestJournalSection;
